/**
 * Centralized Gas Sponsorship Configuration
 *
 * This file defines all gas sponsorship settings for different chains.
 * Adding support for a new chain is as simple as adding a new entry here.
 */

import { PIMLICO_API_KEY } from "@/config/env"
import { optimismSepolia, optimism, berachain, arbitrum } from "viem/chains"

// Gas sponsorship provider types
export type GasSponsorshipProvider =
  | "pimlico"
  | "alchemy"
  | "biconomy"
  | "stackup"

// Gas sponsorship implementation styles
export type GasSponsorshipStyle =
  | "azuro-style-pimlico"
  | "native-aa"
  | "biconomy-aa"
  | "alchemy-aa"

// Configuration for a single chain's gas sponsorship
export interface ChainGasSponsorshipConfig {
  chainId: number
  chainName: string
  enabled: boolean
  provider: GasSponsorshipProvider
  style: GasSponsorshipStyle

  // Provider-specific configuration
  config: {
    // Pimlico configuration
    bundlerUrl?: string
    paymasterUrl?: string
    apiKeyEnvVar?: string

    // Biconomy configuration
    biconomyApiKey?: string

    // Alchemy configuration
    alchemyApiKey?: string

    // Custom configuration
    customConfig?: Record<string, any>
  }

  // Feature flags for this chain
  features: {
    sponsorApprovals?: boolean
    sponsorBets?: boolean
    sponsorSwaps?: boolean
    sponsorRedeems?: boolean
    sponsorWithdraws?: boolean
    sponsorAll?: boolean
  }

  // Optional fallback configuration
  fallback?: {
    provider?: GasSponsorshipProvider
    style?: GasSponsorshipStyle
  }
}

// Master configuration for all supported chains
export const GAS_SPONSORSHIP_CONFIG: Record<number, ChainGasSponsorshipConfig> =
  {
    // OP Sepolia - Currently active
    [optimismSepolia.id]: {
      chainId: optimismSepolia.id,
      chainName: optimismSepolia.name,
      enabled: true,
      provider: "pimlico",
      style: "azuro-style-pimlico",
      config: {
        bundlerUrl: `https://api.pimlico.io/v2/${optimismSepolia.id}/rpc`,
        paymasterUrl: `https://api.pimlico.io/v2/${optimismSepolia.id}/rpc`,
        apiKeyEnvVar: PIMLICO_API_KEY,
      },
      features: {
        sponsorApprovals: true,
        sponsorBets: true,
        sponsorSwaps: false,
        sponsorRedeems: true,
        sponsorWithdraws: true,
        sponsorAll: false,
      },
    },

    // OP Mainnet - Currently active
    [optimism.id]: {
      chainId: optimism.id,
      chainName: optimism.name,
      enabled: true,
      provider: "pimlico",
      style: "azuro-style-pimlico",
      config: {
        bundlerUrl: `https://api.pimlico.io/v2/${optimism.id}/rpc`,
        paymasterUrl: `https://api.pimlico.io/v2/${optimism.id}/rpc`,
        apiKeyEnvVar: PIMLICO_API_KEY,
      },
      features: {
        sponsorApprovals: true,
        sponsorBets: true,
        sponsorSwaps: false,
        sponsorRedeems: true,
        sponsorWithdraws: true,
        sponsorAll: false,
      },
    },

    // Arbitrum Sepolia - Currently active
    [arbitrum.id]: {
      chainId: arbitrum.id,
      chainName: arbitrum.name,
      enabled: true,
      provider: "pimlico",
      style: "azuro-style-pimlico",
      config: {
        bundlerUrl: `https://api.pimlico.io/v2/${arbitrum.id}/rpc`,
        paymasterUrl: `https://api.pimlico.io/v2/${arbitrum.id}/rpc`,
        apiKeyEnvVar: PIMLICO_API_KEY,
      },
      features: {
        sponsorApprovals: true,
        sponsorBets: true,
        sponsorSwaps: false,
        sponsorRedeems: true,
        sponsorWithdraws: true,
        sponsorAll: false,
      },
    },

    // Berachain - Currently active

    [berachain.id]: {
      chainId: berachain.id,
      chainName: berachain.name,
      enabled: true,
      provider: "pimlico",
      style: "azuro-style-pimlico",
      config: {
        bundlerUrl: `https://api.pimlico.io/v2/${berachain.id}/rpc`,
        paymasterUrl: `https://api.pimlico.io/v2/${berachain.id}/rpc`,
        apiKeyEnvVar: PIMLICO_API_KEY,
      },
      features: {
        sponsorApprovals: true,
        sponsorBets: true,
        sponsorSwaps: false,
        sponsorRedeems: true,
        sponsorWithdraws: true,
        sponsorAll: false,
      },
    },

    // Add more chains as needed...
    // [polygon.id]: { ... },
    // [mainnet.id]: { ... },
  }

// Global gas sponsorship settings
export const GAS_SPONSORSHIP_GLOBAL_CONFIG = {
  // Master enable/disable switch
  enabled: true,

  // Default settings for new chains
  defaults: {
    provider: "pimlico" as GasSponsorshipProvider,
    style: "azuro-style-pimlico" as GasSponsorshipStyle,
    features: {
      sponsorApprovals: true,
      sponsorBets: true,
      sponsorSwaps: false,
      sponsorRedeems: true,
      sponsorWithdraws: true,
      sponsorAll: false,
    },
  },

  // Debugging and development
  debug: {
    enabled: process.env.NODE_ENV === "development",
    verboseLogging: true,
    showDebugInfo: true,
  },
} as const

// Simple cache for gas sponsorship support checks
const gasSponsorshipCache = new Map<number, boolean>()
const chainConfigCache = new Map<number, ChainGasSponsorshipConfig | null>()
const providerConfigCache = new Map<number, any>()

// Utility functions for working with the configuration

/**
 * Get gas sponsorship configuration for a specific chain
 * Uses a simple cache to avoid repeated lookups
 */
export const getChainGasSponsorshipConfig = (
  chainId: number
): ChainGasSponsorshipConfig | null => {
  // Check cache first
  if (chainConfigCache.has(chainId)) {
    return chainConfigCache.get(chainId)!
  }

  // Get configuration
  const config = GAS_SPONSORSHIP_CONFIG[chainId] || null

  // Cache the result
  chainConfigCache.set(chainId, config)

  return config
}

/**
 * Check if gas sponsorship is supported and enabled for a chain
 * Uses a simple cache to avoid repeated lookups
 */
export const isGasSponsorshipSupported = (chainId: number): boolean => {
  // Check cache first
  if (gasSponsorshipCache.has(chainId)) {
    return gasSponsorshipCache.get(chainId)!
  }

  // Calculate result
  const config = getChainGasSponsorshipConfig(chainId)
  const result =
    !!config && config.enabled && GAS_SPONSORSHIP_GLOBAL_CONFIG.enabled

  // Cache the result
  gasSponsorshipCache.set(chainId, result)

  return result
}

/**
 * Check if a specific feature is supported for a chain
 */
export const isFeatureSupported = (
  chainId: number,
  feature: keyof ChainGasSponsorshipConfig["features"]
): boolean => {
  const config = getChainGasSponsorshipConfig(chainId)
  if (!config || !config.enabled) {
    return false
  }

  return config.features[feature] || config.features.sponsorAll || false
}

/**
 * Get all supported chain IDs
 */
export const getSupportedChainIds = (): number[] => {
  return Object.keys(GAS_SPONSORSHIP_CONFIG)
    .map(Number)
    .filter(chainId => isGasSponsorshipSupported(chainId))
}

/**
 * Get provider configuration for a chain
 * Uses a simple cache to avoid repeated lookups
 */
export const getProviderConfig = (chainId: number) => {
  // Check cache first
  if (providerConfigCache.has(chainId)) {
    return providerConfigCache.get(chainId)!
  }

  const config = getChainGasSponsorshipConfig(chainId)
  if (!config) {
    const result = null
    providerConfigCache.set(chainId, result)
    return result
  }

  const result = {
    provider: config.provider,
    style: config.style,
    config: config.config,
    fallback: config.fallback,
  }

  // Cache the result
  providerConfigCache.set(chainId, result)

  return result
}

/**
 * Get environment variable value for a chain's API key
 */
export const getApiKey = (chainId: number): string | null => {
  const config = getChainGasSponsorshipConfig(chainId)
  if (!config?.config.apiKeyEnvVar) {
    return null
  }

  return GAS_SPONSORSHIP_CONFIG[chainId]?.config.apiKeyEnvVar || null
}

/**
 * Helper to quickly add a new chain configuration
 */
export const createChainConfig = (
  chainId: number,
  chainName: string,
  provider: GasSponsorshipProvider = "pimlico",
  style: GasSponsorshipStyle = "azuro-style-pimlico"
): ChainGasSponsorshipConfig => {
  return {
    chainId,
    chainName,
    enabled: false, // Start disabled by default
    provider,
    style,
    config: {
      bundlerUrl: `https://api.pimlico.io/v2/${chainId}/rpc`,
      paymasterUrl: `https://api.pimlico.io/v2/${chainId}/rpc`,
      apiKeyEnvVar: PIMLICO_API_KEY,
    },
    features: { ...GAS_SPONSORSHIP_GLOBAL_CONFIG.defaults.features },
  }
}

// Export supported chain IDs for easy imports
export const SUPPORTED_GAS_SPONSORSHIP_CHAINS = getSupportedChainIds()

// Export commonly used chain configs for convenience
export const OP_MAINNET_CONFIG = GAS_SPONSORSHIP_CONFIG[optimism.id]
export const ARBITRUM_CONFIG = GAS_SPONSORSHIP_CONFIG[arbitrum.id]
export const BERACHAIN_CONFIG = GAS_SPONSORSHIP_CONFIG[berachain.id]
